// Rule values come from shared/passwordPolicy.json via the generated module; only the logic lives here.
import {
	PASSWORD_COMMON_PASSWORDS,
	PASSWORD_MIN_LENGTH,
	PASSWORD_REQUIRE_LOWERCASE,
	PASSWORD_REQUIRE_NUMBER,
	PASSWORD_REQUIRE_SPECIAL_CHARACTER,
	PASSWORD_REQUIRE_UPPERCASE,
	PASSWORD_SPECIAL_CHARACTERS,
} from './passwordPolicy.generated'

export { PASSWORD_MIN_LENGTH }

export interface PasswordIdentity {
	name?: string
	email?: string
}

export const PASSWORD_TOO_SHORT_MESSAGE = `Your password must be at least ${PASSWORD_MIN_LENGTH} characters.`
export const PASSWORD_COMMON_MESSAGE =
	'Choose a less common password and avoid using your name or email address.'
export const PASSWORD_REQUIREMENTS_MESSAGE = `Your password must include at least one of each of the following:
- capital letter
- lowercase letter
- number
- special character (${PASSWORD_SPECIAL_CHARACTERS.split('').join(' ')})`

export const PASSWORD_HINT = `At least ${PASSWORD_MIN_LENGTH} characters. One capital letter, lowercase letter, number, and special character.`

export const PASSWORD_MISMATCH_MESSAGE = 'Your passwords do not match'

function normalized(value: string): string {
	return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function containsIdentity(password: string, identity?: PasswordIdentity): boolean {
	if (!identity) return false

	const emailLocalPart = identity.email?.split('@')[0] ?? ''
	const identityParts = [
		...(identity.name?.split(/[^a-z0-9]+/i) ?? []),
		...emailLocalPart.split(/[^a-z0-9]+/i),
	]
		.map(normalized)
		.filter((part) => part.length >= 3)
	const normalizedPassword = normalized(password)
	return identityParts.some((part) => normalizedPassword.includes(part))
}

export function getPasswordError(password: string, identity?: PasswordIdentity): string | null {
	if (password.length < PASSWORD_MIN_LENGTH) return PASSWORD_TOO_SHORT_MESSAGE

	const missing: string[] = []
	if (PASSWORD_REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) missing.push('capital letter')
	if (PASSWORD_REQUIRE_LOWERCASE && !/[a-z]/.test(password)) missing.push('lowercase letter')
	if (PASSWORD_REQUIRE_NUMBER && !/[0-9]/.test(password)) missing.push('number')
	if (
		PASSWORD_REQUIRE_SPECIAL_CHARACTER &&
		![...PASSWORD_SPECIAL_CHARACTERS].some((character) => password.includes(character))
	) {
		missing.push(`special character (${PASSWORD_SPECIAL_CHARACTERS.split('').join(' ')})`)
	}
	if (missing.length > 0) {
		return `Your password must include at least one of each of the following:\n- ${missing.join('\n- ')}`
	}

	const normalizedPassword = normalized(password)
	if (
		PASSWORD_COMMON_PASSWORDS.includes(normalizedPassword) ||
		containsIdentity(password, identity)
	) {
		return PASSWORD_COMMON_MESSAGE
	}
	return null
}

// Returns the first problem with a new-password/confirm pair, or null when both are acceptable.
export function getNewPasswordError(
	password: string,
	confirmPassword: string,
	identity?: PasswordIdentity
): string | null {
	return (
		getPasswordError(password, identity) ??
		(password !== confirmPassword ? PASSWORD_MISMATCH_MESSAGE : null)
	)
}
